import { useState, useId } from 'react';
import { Markdown } from './Markdown.js';

interface MarkdownEditorProps {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  id?: string;
  ariaLabel?: string;
}

/** 編集 / プレビュー のタブ切替 textarea。プレビューは shared/Markdown で安全描画 */
export function MarkdownEditor({
  value,
  onChange,
  rows = 6,
  required = false,
  placeholder,
  maxLength,
  id,
  ariaLabel,
}: MarkdownEditorProps) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const autoId = useId();
  const editTabId = `${id ?? autoId}-tab-edit`;
  const previewTabId = `${id ?? autoId}-tab-preview`;
  const panelId = `${id ?? autoId}-panel`;

  const isEdit = mode === 'edit';
  const isPreview = mode === 'preview';

  return (
    <div className="md-editor">
      <div className="md-editor-tabs" role="tablist" aria-label="本文編集モード">
        <button
          type="button"
          role="tab"
          id={editTabId}
          aria-selected={isEdit}
          aria-controls={panelId}
          className="md-editor-tab"
          onClick={() => setMode('edit')}
        >
          編集
        </button>
        <button
          type="button"
          role="tab"
          id={previewTabId}
          aria-selected={isPreview}
          aria-controls={panelId}
          className="md-editor-tab"
          onClick={() => setMode('preview')}
        >
          プレビュー
        </button>
      </div>
      <div
        className="md-editor-body"
        role="tabpanel"
        id={panelId}
        aria-labelledby={isEdit ? editTabId : previewTabId}
      >
        <textarea
          id={id}
          aria-label={ariaLabel}
          required={required}
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          style={isPreview ? { display: 'none' } : undefined}
        />
        {isPreview && (
          <div className="md-preview">
            {value.trim().length > 0 ? (
              <Markdown>{value}</Markdown>
            ) : (
              <p className="md-preview-empty mono">プレビューする内容がありません</p>
            )}
          </div>
        )}
      </div>
      <div className="md-editor-foot mono">
        <span>Markdown 記法が使えます</span>
        {maxLength !== undefined && (
          <span>
            {value.length} / {maxLength}
          </span>
        )}
      </div>
    </div>
  );
}
